import React, { useCallback, useMemo, useRef, useState } from "react";
import { Upload, Image as ImageIcon, X, FolderUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

function bytesToSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    sizes.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)) || 0,
  );
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

export default function Dropzone({
  mode,
  files,
  setFiles,
  accept = "image/*",
  maxFiles,
}) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const canAddMore = useMemo(() => {
    if (typeof maxFiles !== "number") return true;
    return files.length < maxFiles;
  }, [files.length, maxFiles]);

  const onPick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const addFiles = useCallback(
    (incoming) => {
      const list = Array.from(incoming || []);
      const images = list.filter((f) => f.type?.startsWith("image/"));
      if (mode === "single") {
        setFiles(images.slice(0, 1));
        return;
      }
      const next = [...files, ...images];
      if (typeof maxFiles === "number") setFiles(next.slice(0, maxFiles));
      else setFiles(next);
    },
    [files, maxFiles, mode, setFiles],
  );

  const onChange = useCallback(
    (e) => {
      addFiles(e.target.files);
      e.target.value = "";
    },
    [addFiles],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (!canAddMore) return;
      if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    },
    [addFiles, canAddMore],
  );

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const removeAt = useCallback(
    (idx) => {
      const next = files.filter((_, i) => i !== idx);
      setFiles(next);
    },
    [files, setFiles],
  );

  return (
    <div className="space-y-4" data-testid={`dropzone-${mode}-wrapper`}>
      <input
        data-testid={`dropzone-${mode}-file-input`}
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={mode === "batch"}
        onChange={onChange}
        className="hidden"
      />

      <div
        data-testid={`dropzone-${mode}-area`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        className={cn(
          "relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-950 to-zinc-950/40 p-6",
          "shadow-[0_10px_30px_rgba(0,0,0,0.35)]",
          dragActive
            ? "ring-2 ring-violet-400/60"
            : "ring-1 ring-transparent",
        )}
      >
        <div
          className={cn(
            "pointer-events-none absolute inset-0 opacity-0",
            dragActive ? "opacity-100" : "opacity-0",
          )}
          style={{
            background:
              "radial-gradient(800px 220px at 50% 0%, rgba(139,92,246,0.18), transparent 60%)",
          }}
        />

        <div className="relative flex items-start gap-4">
          <div
            className="grid h-12 w-12 place-items-center rounded-2xl bg-white/5 ring-1 ring-white/10"
            data-testid={`dropzone-${mode}-icon`}
          >
            {mode === "batch" ? (
              <FolderUp className="h-6 w-6 text-violet-200" />
            ) : (
              <Upload className="h-6 w-6 text-violet-200" />
            )}
          </div>
          <div className="flex-1">
            <div
              className="text-base font-medium text-zinc-100"
              data-testid={`dropzone-${mode}-title`}
            >
              Drag & drop {mode === "batch" ? "images" : "an image"} here
            </div>
            <div
              className="mt-1 text-sm text-zinc-400"
              data-testid={`dropzone-${mode}-subtitle`}
            >
              {mode === "batch"
                ? "Or pick multiple files. We will export a ZIP of transparent PNGs."
                : "Or pick one file. We will export a transparent PNG."}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={onPick}
                disabled={!canAddMore}
                className="rounded-full bg-violet-500 text-white hover:bg-violet-400"
                data-testid={`dropzone-${mode}-pick-button`}
              >
                Choose {mode === "batch" ? "files" : "file"}
              </Button>
              <div
                className="text-xs text-zinc-500"
                data-testid={`dropzone-${mode}-hint`}
              >
                Supported: PNG, JPG, WEBP
              </div>
            </div>
          </div>
        </div>
      </div>

      {files.length > 0 ? (
        <div
          className="rounded-2xl border border-white/10 bg-zinc-950/40 p-4"
          data-testid={`dropzone-${mode}-filelist`}
        >
          <div
            className="mb-3 text-sm font-medium text-zinc-200"
            data-testid={`dropzone-${mode}-filelist-title`}
          >
            Selected {files.length} file{files.length === 1 ? "" : "s"}
          </div>
          <div className="space-y-2">
            {files.map((f, idx) => (
              <div
                key={`${f.name}-${idx}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2"
                data-testid={`dropzone-${mode}-file-row-${idx}`}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-zinc-300" />
                  <div className="min-w-0">
                    <div
                      className="truncate text-sm text-zinc-100"
                      data-testid={`dropzone-${mode}-file-name-${idx}`}
                    >
                      {f.name}
                    </div>
                    <div
                      className="text-xs text-zinc-400"
                      data-testid={`dropzone-${mode}-file-size-${idx}`}
                    >
                      {bytesToSize(f.size)}
                    </div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAt(idx)}
                  className="rounded-full text-zinc-200 hover:bg-white/10"
                  data-testid={`dropzone-${mode}-remove-file-button-${idx}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
